import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Activity,
  AlertTriangle,
  Building2,
  Clock3,
  Euro,
  RefreshCcw,
  TrendingDown,
  TrendingUp,
  UserRoundCheck,
  UsersRound,
  MapPinned,
  CircleAlert,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const PROBLEM_STATUSES = new Set([
  'refused',
  'cancelled',
  'canceled',
  'rejected',
  'failed',
  'no_show',
]);

const money = (value) =>
  new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const integer = (value) =>
  new Intl.NumberFormat('fr-BE', { maximumFractionDigits: 0 }).format(
    Number(value || 0)
  );

const oneDecimal = (value) =>
  new Intl.NumberFormat('fr-BE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Number(value || 0));

const percentText = (value) =>
  `${Number(value || 0).toFixed(1).replace('.', ',')} %`;

const getDate = (p) => {
  const raw = p.ended_at || p.dateEnd || p.dateStart || p.started_at || p.created_at;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const isCompleted = (p) => String(p.status || '').toLowerCase() === 'completed';

const getInstitutionId = (p) =>
  p.etablissement_id ||
  p.institution_id ||
  p.institution ||
  p.etablissement?.id ||
  p.etablissement?._id ||
  null;

const getInstitutionName = (p, institutionMap) => {
  const id = getInstitutionId(p);

  return (
    p.institution_name ||
    p.etablissement_name ||
    p.etablissement?.name ||
    institutionMap.get(String(id))?.name ||
    'Établissement non renseigné'
  );
};

const getRegion = (p, institutionMap) => {
  const id = getInstitutionId(p);
  const institution = id ? institutionMap.get(String(id)) : null;

  if (!institution) return 'Zone non renseignée';

  const explicit =
    institution.region ||
    institution.province ||
    institution.city ||
    institution.commune ||
    institution.zone;

  if (explicit) return String(explicit);

  const address = institution.address || institution.adresse || '';

  if (!address) return 'Zone non renseignée';

  const parts = String(address)
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

  return parts.length > 1
    ? parts[parts.length - 1]
    : parts[0] || 'Zone non renseignée';
};

const getRevenue = (p) => {
  if (!isCompleted(p)) return 0;
  return Number(p.final_rate || 0);
};

const getWorkedMinutes = (p) => {
  if (!isCompleted(p)) return 0;

  const actual = Number(p.actual_duration);
  if (Number.isFinite(actual) && actual > 0) return actual;

  const planned = Number(p.dureeMinutes);
  return Number.isFinite(planned) ? planned : 0;
};

const pctChange = (current, previous) => {
  const c = Number(current || 0);
  const p = Number(previous || 0);

  if (p === 0) return c > 0 ? 100 : 0;

  return ((c - p) / p) * 100;
};

function TrendBadge({ value, inverse = false }) {
  const numeric = Number(value || 0);
  const good = inverse ? numeric <= 0 : numeric >= 0;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold border ${
        good
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
          : 'bg-red-50 text-red-700 border-red-200'
      }`}
    >
      {numeric >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
      {numeric > 0 ? '+' : ''}
      {percentText(numeric)}
    </span>
  );
}

function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  inverseTrend = false,
}) {
  return (
    <Card className="border border-[#D4EBEB] bg-white shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.12em] font-semibold text-[#5E7D7D]">
              {title}
            </p>

            <div className="mt-2 text-3xl font-black text-[#234B4B] truncate">
              {value}
            </div>
          </div>

          <div className="h-11 w-11 rounded-2xl bg-[#EAF5F5] text-[#234B4B] flex items-center justify-center shrink-0">
            <Icon size={22} />
          </div>
        </div>

        <div className="mt-4 min-h-[28px] flex flex-wrap items-center gap-2">
          {trend != null && (
            <TrendBadge value={trend} inverse={inverseTrend} />
          )}
          {subtitle && <span className="text-xs text-gray-500">{subtitle}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ children = 'Aucune donnée disponible.' }) {
  return (
    <div className="h-56 flex items-center justify-center text-sm text-gray-400 text-center px-6">
      {children}
    </div>
  );
}

function RevenueLineChart({ data }) {
  if (!data.length || data.every((item) => Number(item.value || 0) === 0)) {
    return (
      <EmptyState>
        Aucun chiffre d'affaires réalisé sur cette période.
      </EmptyState>
    );
  }

  const width = 900;
  const height = 260;
  const padX = 55;
  const padY = 28;

  const max = Math.max(...data.map((d) => Number(d.value || 0)), 1);

  const points = data.map((item, index) => {
    const x =
      data.length === 1
        ? width / 2
        : padX + (index * (width - padX * 2)) / (data.length - 1);

    const y =
      height -
      padY -
      (Number(item.value || 0) / max) * (height - padY * 2);

    return { ...item, x, y };
  });

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full min-w-[680px] h-[270px]"
        role="img"
        aria-label="Graphique d'évolution du chiffre d'affaires"
      >
        {[0.25, 0.5, 0.75, 1].map((ratio) => {
          const y =
            height - padY - ratio * (height - padY * 2);

          return (
            <g key={ratio}>
              <line
                x1={padX}
                x2={width - padX}
                y1={y}
                y2={y}
                stroke="#DCEAEA"
                strokeWidth="1"
              />
              <text x="3" y={y + 4} fontSize="11" fill="#6B7F7F">
                {Math.round(max * ratio)} €
              </text>
            </g>
          );
        })}

        <polyline
          points={points.map((p) => `${p.x},${p.y}`).join(' ')}
          fill="none"
          stroke="#3E6666"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {points.map((point, index) => {
          const showLabel =
            data.length <= 10 ||
            index === 0 ||
            index === data.length - 1 ||
            index % Math.ceil(data.length / 7) === 0;

          return (
            <g key={`${point.label}-${index}`}>
              <circle
                cx={point.x}
                cy={point.y}
                r="5"
                fill="#234B4B"
              />

              {showLabel && (
                <text
                  x={point.x}
                  y={height - 5}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#617777"
                >
                  {point.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function HorizontalBars({ items }) {
  if (!items.length) {
    return <EmptyState />;
  }

  const max = Math.max(...items.map((item) => Number(item.value || 0)), 1);

  return (
    <div className="space-y-4">
      {items.map((item) => {
        const width = Math.max(
          3,
          (Number(item.value || 0) / max) * 100
        );

        return (
          <div key={item.label}>
            <div className="flex items-center justify-between gap-3 mb-1.5">
              <span className="text-sm font-medium text-gray-700 truncate">
                {item.label}
              </span>
              <span className="text-sm font-black text-[#234B4B] shrink-0">
                {integer(item.value)}
              </span>
            </div>

            <div className="h-2.5 rounded-full bg-[#EDF5F5] overflow-hidden">
              <div
                className="h-full rounded-full bg-[#7FAEAE]"
                style={{ width: `${width}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusChip({ label, value, tone = 'neutral' }) {
  const className =
    tone === 'danger'
      ? 'border-red-200 bg-red-50 text-red-700'
      : tone === 'warning'
      ? 'border-orange-200 bg-orange-50 text-orange-700'
      : tone === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : 'border-[#D4EBEB] bg-[#F4FAFA] text-[#234B4B]';

  return (
    <div className={`rounded-2xl border p-4 ${className}`}>
      <p className="text-xs font-semibold uppercase tracking-wide opacity-80">
        {label}
      </p>
      <p className="mt-1 text-2xl font-black">{integer(value)}</p>
    </div>
  );
}

export default function DashboardAdmin() {
  const { axiosInstance, user } = useAuth();
  

  const today = new Date();

  const [prestations, setPrestations] = useState([]);
  const [users, setUsers] = useState([]);
  const [institutions, setInstitutions] = useState([]);

  const [loading, setLoading] = useState(true);

  const [selectedMonth, setSelectedMonth] = useState(today.getMonth());
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());

  const [trendDays, setTrendDays] = useState(30);

  const loadDashboard = async () => {
    setLoading(true);

    try {
      const [prestationsResponse, usersResponse, institutionsResponse] =
        await Promise.all([
          axiosInstance.get('/prestations'),
          axiosInstance.get('/users'),
          axiosInstance.get('/institutions'),
        ]);

      setPrestations(
        Array.isArray(prestationsResponse.data)
          ? prestationsResponse.data.filter(
              (p) => String(p.status || '').toLowerCase() !== 'deleted'
            )
          : []
      );

      setUsers(
        Array.isArray(usersResponse.data)
          ? usersResponse.data
          : []
      );

      setInstitutions(
        Array.isArray(institutionsResponse.data)
          ? institutionsResponse.data
          : []
      );
    } catch (error) {
      console.error('Dashboard admin error:', error);
      toast.error("Impossible de charger le tableau de bord Direction");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      loadDashboard();
    }
  }, []);

  const institutionMap = useMemo(() => {
    const map = new Map();

    institutions.forEach((institution) => {
      const id = institution.id || institution._id;

      if (id) {
        map.set(String(id), institution);
      }
    });

    return map;
  }, [institutions]);

  const currentPeriod = useMemo(() => {
    return prestations.filter((p) => {
      const date = getDate(p);

      return (
        date &&
        date.getFullYear() === Number(selectedYear) &&
        date.getMonth() === Number(selectedMonth)
      );
    });
  }, [prestations, selectedMonth, selectedYear]);

  const previousMonthDate = useMemo(
    () => new Date(selectedYear, selectedMonth - 1, 1),
    [selectedYear, selectedMonth]
  );

  const previousPeriod = useMemo(() => {
    return prestations.filter((p) => {
      const date = getDate(p);

      return (
        date &&
        date.getFullYear() === previousMonthDate.getFullYear() &&
        date.getMonth() === previousMonthDate.getMonth()
      );
    });
  }, [prestations, previousMonthDate]);

  const stats = useMemo(() => {
    const completed = currentPeriod.filter(isCompleted);
    const previousCompleted = previousPeriod.filter(isCompleted);

    const revenue = completed.reduce(
      (sum, p) => sum + getRevenue(p),
      0
    );

    const previousRevenue = previousCompleted.reduce(
      (sum, p) => sum + getRevenue(p),
      0
    );

    const workedMinutes = completed.reduce(
      (sum, p) => sum + getWorkedMinutes(p),
      0
    );

    const previousWorkedMinutes = previousCompleted.reduce(
      (sum, p) => sum + getWorkedMinutes(p),
      0
    );

    const activeInstitutions = new Set(
      currentPeriod
        .map(getInstitutionId)
        .filter(Boolean)
        .map(String)
    );

    const previousActiveInstitutions = new Set(
      previousPeriod
        .map(getInstitutionId)
        .filter(Boolean)
        .map(String)
    );

    const activeCaregivers = new Set(
      currentPeriod
        .map((p) => p.assigned_to)
        .filter(Boolean)
        .map(String)
    );

    const previousActiveCaregivers = new Set(
      previousPeriod
        .map((p) => p.assigned_to)
        .filter(Boolean)
        .map(String)
    );

    const pending = currentPeriod.filter(
      (p) => String(p.status || '').toLowerCase() === 'pending'
    ).length;

    const assigned = currentPeriod.filter(
      (p) => String(p.status || '').toLowerCase() === 'assigned'
    ).length;

    const accepted = currentPeriod.filter(
      (p) => String(p.status || '').toLowerCase() === 'accepted'
    ).length;

    const inProgress = currentPeriod.filter(
      (p) => String(p.status || '').toLowerCase() === 'in_progress'
    ).length;

    const refused = currentPeriod.filter(
      (p) => String(p.status || '').toLowerCase() === 'refused'
    ).length;

    const cancelled = currentPeriod.filter((p) => {
      const status = String(p.status || '').toLowerCase();
      return status === 'cancelled' || status === 'canceled';
    }).length;

    const problematic = currentPeriod.filter((p) => {
      const status = String(p.status || '').toLowerCase();

      return (
        PROBLEM_STATUSES.has(status) ||
        p.problematic === true ||
        p.has_issue === true ||
        p.incident === true
      );
    }).length;

    const previousProblematic = previousPeriod.filter((p) => {
      const status = String(p.status || '').toLowerCase();

      return (
        PROBLEM_STATUSES.has(status) ||
        p.problematic === true ||
        p.has_issue === true ||
        p.incident === true
      );
    }).length;

    const urgent = currentPeriod.filter(
      (p) =>
        String(p.prestation_type || '').toLowerCase() === 'urgent'
    ).length;

    const unassigned = currentPeriod.filter((p) => {
      const status = String(p.status || '').toLowerCase();

      return (
        !p.assigned_to &&
        !['completed', 'refused', 'cancelled', 'canceled'].includes(status)
      );
    }).length;

    const longRunning = currentPeriod.filter((p) => {
      const status = String(p.status || '').toLowerCase();

      if (status !== 'in_progress' || !p.started_at) return false;

      const startedAt = new Date(p.started_at);

      if (Number.isNaN(startedAt.getTime())) return false;

      return Date.now() - startedAt.getTime() > 12 * 60 * 60 * 1000;
    }).length;

    const delayed = currentPeriod.filter(
      (p) => Number(p.delay_minutes || 0) > 0
    ).length;

    const approvedCaregivers = users.filter((u) => {
      const role = String(u.role || '').toLowerCase();

      return (
        u.status === 'approved' &&
        ['nurse', 'caregiver'].includes(role)
      );
    }).length;

    const pendingUsers = users.filter(
      (u) => u.status === 'pending'
    ).length;

    return {
      revenue,
      revenueTrend: pctChange(revenue, previousRevenue),

      completed: completed.length,
      completedTrend: pctChange(
        completed.length,
        previousCompleted.length
      ),

      workedMinutes,
      workedTrend: pctChange(
        workedMinutes,
        previousWorkedMinutes
      ),

      institutions: activeInstitutions.size,
      institutionsTrend: pctChange(
        activeInstitutions.size,
        previousActiveInstitutions.size
      ),

      activeCaregivers: activeCaregivers.size,
      activeCaregiversTrend: pctChange(
        activeCaregivers.size,
        previousActiveCaregivers.size
      ),

      pending,
      assigned,
      accepted,
      inProgress,
      refused,
      cancelled,
      problematic,
      problematicTrend: pctChange(
        problematic,
        previousProblematic
      ),
      urgent,
      unassigned,
      longRunning,
      delayed,

      approvedCaregivers,
      pendingUsers,

      totalPeriod: currentPeriod.length,
    };
  }, [currentPeriod, previousPeriod, users]);

  const regionalActivity = useMemo(() => {
    const counts = new Map();

    currentPeriod.forEach((p) => {
      const label = getRegion(p, institutionMap);

      counts.set(
        label,
        (counts.get(label) || 0) + 1
      );
    });

    return [...counts.entries()]
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [currentPeriod, institutionMap]);

  const topInstitutions = useMemo(() => {
    const counts = new Map();

    currentPeriod.forEach((p) => {
      const name = getInstitutionName(p, institutionMap);

      counts.set(
        name,
        (counts.get(name) || 0) + 1
      );
    });

    return [...counts.entries()]
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [currentPeriod, institutionMap]);

  const revenueTrendData = useMemo(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);

    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - (trendDays - 1));

    const days = Array.from({ length: trendDays }, (_, index) => {
      const date = new Date(start);

      date.setDate(start.getDate() + index);

      const key = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

      return {
        key,
        label:
          trendDays === 7
            ? date.toLocaleDateString('fr-BE', {
                weekday: 'short',
              })
            : date.toLocaleDateString('fr-BE', {
                day: '2-digit',
                month: '2-digit',
              }),
        value: 0,
      };
    });

    const dataMap = new Map(
      days.map((item) => [item.key, item])
    );

    prestations.forEach((p) => {
      if (!isCompleted(p)) return;

      const date = getDate(p);

      if (!date || date < start || date > end) return;

      const key = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

      const item = dataMap.get(key);

      if (item) {
        item.value += getRevenue(p);
      }
    });

    return days;
  }, [prestations, trendDays]);

  const alerts = useMemo(() => {
    const list = [];

    if (stats.unassigned > 0) {
      list.push({
        title: `${stats.unassigned} prestation(s) sans soignant`,
        description:
          'Des prestations sont encore ouvertes sans professionnel assigné.',
        type: 'danger',
      });
    }

    if (stats.refused > 0) {
      list.push({
        title: `${stats.refused} prestation(s) refusée(s)`,
        description:
          'Ces prestations doivent éventuellement être réattribuées.',
        type: 'warning',
      });
    }

    if (stats.longRunning > 0) {
      list.push({
        title: `${stats.longRunning} prestation(s) en cours depuis plus de 12 h`,
        description:
          'Vérifier si leur clôture a été oubliée.',
        type: 'danger',
      });
    }

    if (stats.delayed > 0) {
      list.push({
        title: `${stats.delayed} prestation(s) avec retard enregistré`,
        description:
          'Le backend a enregistré un delay_minutes supérieur à zéro.',
        type: 'warning',
      });
    }

    if (stats.pendingUsers > 0) {
      list.push({
        title: `${stats.pendingUsers} utilisateur(s) en attente`,
        description:
          'Des comptes doivent encore être approuvés ou rejetés.',
        type: 'info',
      });
    }

    if (stats.urgent > 0) {
      list.push({
        title: `${stats.urgent} prestation(s) urgente(s) sur la période`,
        description:
          'Suivre séparément les urgences et leur affectation.',
        type: 'info',
      });
    }

    return list;
  }, [stats]);

  const monthTitle = `${MONTHS[selectedMonth]} ${selectedYear}`;

  const previousMonthTitle = `${
    MONTHS[previousMonthDate.getMonth()]
  } ${previousMonthDate.getFullYear()}`;

  if (user?.role !== 'admin') {
    return (
      <Layout title="Tableau de bord Direction">
        <Card className="border border-red-200">
          <CardContent className="p-8 text-center">
            <AlertTriangle
              className="mx-auto text-red-500"
              size={32}
            />

            <h2 className="mt-3 text-xl font-black text-gray-900">
              Accès réservé à la Direction
            </h2>

            <p className="mt-2 text-gray-500">
              Cette page contient des informations financières et
              administratives réservées aux administrateurs.
            </p>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout title="Tableau de bord Direction">
      <div className="space-y-7">
        <section className="rounded-3xl border border-[#D4EBEB] bg-gradient-to-br from-white via-[#F7FBFB] to-[#E7F2F2] p-6 lg:p-8 shadow-sm">
          <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#D4EBEB] bg-white px-3 py-1.5 text-xs font-bold text-[#3E6666]">
                <Activity size={15} />
                DIRECTION • SANA CARE
              </div>

              <h1 className="mt-4 text-3xl lg:text-4xl font-black text-[#234B4B]">
                Activité SanaCare — {monthTitle}
              </h1>

              <p className="mt-2 max-w-3xl text-gray-600">
                Vue gobale des prestations, du chiffre d'affaires,
                des heures, desinstitutions, dessoignants et qq points
                points de vigilance...
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-4">
                <div>
                  <span className="text-2xl font-black text-[#234B4B]">
                    {integer(stats.completed)}
                  </span>
                  <span className="ml-2 text-sm text-gray-500">
                    prestations réalisées
                  </span>
                </div>

                <TrendBadge value={stats.completedTrend} />

                <div className="hidden md:block w-px h-7 bg-[#C8DDDD]" />

                <div>
                  <span className="text-2xl font-black text-[#234B4B]">
                    {money(stats.revenue)}
                  </span>
                  <span className="ml-2 text-sm text-gray-500">
                    de CA réalisé
                  </span>
                </div>

                <TrendBadge value={stats.revenueTrend} />
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <select
                value={selectedMonth}
                onChange={(e) =>
                  setSelectedMonth(Number(e.target.value))
                }
                className="rounded-xl border border-[#AFCFCF] bg-white px-4 py-3 text-sm font-semibold text-[#234B4B] shadow-sm focus:ring-2 focus:ring-[#7FAEAE]"
              >
                {MONTHS.map((month, index) => (
                  <option key={month} value={index}>
                    {month}
                  </option>
                ))}
              </select>

              <select
                value={selectedYear}
                onChange={(e) =>
                  setSelectedYear(Number(e.target.value))
                }
                className="rounded-xl border border-[#AFCFCF] bg-white px-4 py-3 text-sm font-semibold text-[#234B4B] shadow-sm focus:ring-2 focus:ring-[#7FAEAE]"
              >
                {Array.from(
                  { length: 7 },
                  (_, i) => today.getFullYear() - 4 + i
                ).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>

              <Button
                type="button"
                onClick={loadDashboard}
                disabled={loading}
                variant="outline"
                className="border-[#AFCFCF] bg-white text-[#234B4B]"
              >
                <RefreshCcw
                  size={17}
                  className={`mr-2 ${
                    loading ? 'animate-spin' : ''
                  }`}
                />
                Actualiser
              </Button>
            </div>
          </div>
        </section>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="h-12 w-12 rounded-full border-4 border-[#D4EBEB] border-t-[#234B4B] animate-spin" />
          </div>
        ) : (
          <>
            <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <MetricCard
                title="Chiffre d'affaires réalisé"
                value={money(stats.revenue)}
                subtitle={`vs ${previousMonthTitle}`}
                icon={Euro}
                trend={stats.revenueTrend}
              />

              <MetricCard
                title="Prestations réalisées"
                value={integer(stats.completed)}
                subtitle={`vs ${previousMonthTitle}`}
                icon={UserRoundCheck}
                trend={stats.completedTrend}
              />

              <MetricCard
                title="Heures prestées"
                value={`${oneDecimal(stats.workedMinutes / 60)} h`}
                subtitle={`vs ${previousMonthTitle}`}
                icon={Clock3}
                trend={stats.workedTrend}
              />

              <MetricCard
                title="Institutions actives"
                value={integer(stats.institutions)}
                subtitle={`vs ${previousMonthTitle}`}
                icon={Building2}
                trend={stats.institutionsTrend}
              />

              <MetricCard
                title="Soignants actifs"
                value={integer(stats.activeCaregivers)}
                subtitle="Ayant au moins une prestation sur la période"
                icon={UsersRound}
                trend={stats.activeCaregiversTrend}
              />

              <MetricCard
                title="Soignants approuvés"
                value={integer(stats.approvedCaregivers)}
                subtitle="Infirmiers + aides-soignants approuvés"
                icon={UsersRound}
              />

              <MetricCard
                title="Urgences"
                value={integer(stats.urgent)}
                subtitle="Prestations de type urgent"
                icon={Zap}
              />

              <MetricCard
                title="Problèmes / refus / annulations"
                value={integer(stats.problematic)}
                subtitle={`vs ${previousMonthTitle}`}
                icon={CircleAlert}
                trend={stats.problematicTrend}
                inverseTrend
              />
            </section>

            <section className="grid grid-cols-1 xl:grid-cols-3 gap-5">
              <Card className="xl:col-span-2 border border-[#D4EBEB] shadow-sm">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                    <div>
                      <h2 className="text-xl font-black text-[#234B4B]">
                        Évolution du chiffre d'affaires
                      </h2>
                      <p className="mt-1 text-sm text-gray-500">
                        Prestations terminées uniquement.
                      </p>
                    </div>

                    <div className="flex rounded-xl border border-[#D4EBEB] bg-[#F4FAFA] p-1">
                      {[7, 30].map((days) => (
                        <button
                          key={days}
                          type="button"
                          onClick={() => setTrendDays(days)}
                          className={`px-4 py-2 rounded-lg text-sm font-bold transition ${
                            trendDays === days
                              ? 'bg-white text-[#234B4B] shadow-sm'
                              : 'text-[#628080] hover:text-[#234B4B]'
                          }`}
                        >
                          {days} jours
                        </button>
                      ))}
                    </div>
                  </div>

                  <RevenueLineChart data={revenueTrendData} />
                </CardContent>
              </Card>

              <Card className="border border-[#D4EBEB] shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="h-10 w-10 rounded-xl bg-[#EAF5F5] text-[#234B4B] flex items-center justify-center">
                      <AlertTriangle size={20} />
                    </div>

                    <div>
                      <h2 className="text-xl font-black text-[#234B4B]">
                        À surveiller
                      </h2>
                      <p className="text-sm text-gray-500">
                        Alertes sur {monthTitle}.
                      </p>
                    </div>
                  </div>

                  {alerts.length === 0 ? (
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                      <p className="font-bold text-emerald-800">
                        Aucun signal critique détecté.
                      </p>
                      <p className="mt-1 text-sm text-emerald-700">
                        Les règles d'alerte pourront encore être complétées.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {alerts.map((alert, index) => (
                        <div
                          key={`${alert.title}-${index}`}
                          className={`rounded-2xl border p-4 ${
                            alert.type === 'danger'
                              ? 'border-red-200 bg-red-50'
                              : alert.type === 'warning'
                              ? 'border-orange-200 bg-orange-50'
                              : 'border-blue-200 bg-blue-50'
                          }`}
                        >
                          <p className="font-bold text-gray-900">
                            {alert.title}
                          </p>

                          <p className="mt-1 text-xs text-gray-600">
                            {alert.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </section>

            <section className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <Card className="border border-[#D4EBEB] shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-10 w-10 rounded-xl bg-[#EAF5F5] text-[#234B4B] flex items-center justify-center">
                      <MapPinned size={20} />
                    </div>

                    <div>
                      <h2 className="text-xl font-black text-[#234B4B]">
                        Activité par zone
                      </h2>

                      <p className="text-sm text-gray-500">
                        Basée sur la région/province/ville si disponible,
                        sinon sur l'adresse de l'institution.
                      </p>
                    </div>
                  </div>

                  <HorizontalBars items={regionalActivity} />
                </CardContent>
              </Card>

              <Card className="border border-[#D4EBEB] shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-10 w-10 rounded-xl bg-[#EAF5F5] text-[#234B4B] flex items-center justify-center">
                      <Building2 size={20} />
                    </div>

                    <div>
                      <h2 className="text-xl font-black text-[#234B4B]">
                        Institutions les plus actives
                      </h2>

                      <p className="text-sm text-gray-500">
                        Nombre de prestations sur {monthTitle}.
                      </p>
                    </div>
                  </div>

                  <HorizontalBars items={topInstitutions} />
                </CardContent>
              </Card>
            </section>

            <section className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <Card className="border border-[#D4EBEB] shadow-sm">
                <CardContent className="p-6">
                  <p className="text-xs uppercase tracking-[0.12em] font-semibold text-[#5E7D7D]">
                    Situation des prestations
                  </p>

                  <h2 className="mt-2 text-xl font-black text-[#234B4B]">
                    Statuts — {monthTitle}
                  </h2>

                  <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-3">
                    <StatusChip
                      label="Terminées"
                      value={stats.completed}
                      tone="success"
                    />

                    <StatusChip
                      label="En cours"
                      value={stats.inProgress}
                    />

                    <StatusChip
                      label="Acceptées"
                      value={stats.accepted}
                    />

                    <StatusChip
                      label="Assignées"
                      value={stats.assigned}
                    />

                    <StatusChip
                      label="En attente"
                      value={stats.pending}
                      tone="warning"
                    />

                    <StatusChip
                      label="Refusées"
                      value={stats.refused}
                      tone="danger"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-[#D4EBEB] shadow-sm">
                <CardContent className="p-6">
                  <p className="text-xs uppercase tracking-[0.12em] font-semibold text-[#5E7D7D]">
                    Comparaison mensuelle
                  </p>

                  <h2 className="mt-2 text-xl font-black text-[#234B4B]">
                    {monthTitle} vs {previousMonthTitle}
                  </h2>

                  <div className="mt-6 space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-600">
                        Chiffre d'affaires
                      </span>
                      <TrendBadge value={stats.revenueTrend} />
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-600">
                        Prestations réalisées
                      </span>
                      <TrendBadge value={stats.completedTrend} />
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-600">
                        Heures prestées
                      </span>
                      <TrendBadge value={stats.workedTrend} />
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-600">
                        Institutions actives
                      </span>
                      <TrendBadge value={stats.institutionsTrend} />
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-600">
                        Soignants actifs
                      </span>
                      <TrendBadge value={stats.activeCaregiversTrend} />
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-600">
                        Problèmes / refus / annulations
                      </span>
                      <TrendBadge
                        value={stats.problematicTrend}
                        inverse
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            <section className="grid grid-cols-1 xl:grid-cols-3 gap-5">
              <Card className="border border-[#D4EBEB] shadow-sm">
                <CardContent className="p-6">
                  <p className="text-xs uppercase tracking-[0.12em] font-semibold text-[#5E7D7D]">
                    Organisation
                  </p>

                  <h2 className="mt-2 text-xl font-black text-[#234B4B]">
                    Affectation des prestations
                  </h2>

                  <div className="mt-5 space-y-3">
                    <div className="flex items-center justify-between rounded-xl bg-[#F7FBFB] p-3">
                      <span className="text-sm text-gray-600">
                        Prestations totales
                      </span>
                      <b className="text-[#234B4B]">
                        {integer(stats.totalPeriod)}
                      </b>
                    </div>

                    <div className="flex items-center justify-between rounded-xl bg-[#F7FBFB] p-3">
                      <span className="text-sm text-gray-600">
                        Sans soignant
                      </span>
                      <b className="text-[#234B4B]">
                        {integer(stats.unassigned)}
                      </b>
                    </div>

                    <div className="flex items-center justify-between rounded-xl bg-[#F7FBFB] p-3">
                      <span className="text-sm text-gray-600">
                        Prestations urgentes
                      </span>
                      <b className="text-[#234B4B]">
                        {integer(stats.urgent)}
                      </b>
                    </div>

                    <div className="flex items-center justify-between rounded-xl bg-[#F7FBFB] p-3">
                      <span className="text-sm text-gray-600">
                        Avec retard
                      </span>
                      <b className="text-[#234B4B]">
                        {integer(stats.delayed)}
                      </b>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-[#D4EBEB] shadow-sm">
                <CardContent className="p-6">
                  <p className="text-xs uppercase tracking-[0.12em] font-semibold text-[#5E7D7D]">
                    Équipe
                  </p>

                  <h2 className="mt-2 text-xl font-black text-[#234B4B]">
                    Situation des soignants
                  </h2>

                  <div className="mt-5 space-y-3">
                    <div className="flex items-center justify-between rounded-xl bg-[#F7FBFB] p-3">
                      <span className="text-sm text-gray-600">
                        Soignants approuvés
                      </span>
                      <b className="text-[#234B4B]">
                        {integer(stats.approvedCaregivers)}
                      </b>
                    </div>

                    <div className="flex items-center justify-between rounded-xl bg-[#F7FBFB] p-3">
                      <span className="text-sm text-gray-600">
                        Soignants actifs ce mois
                      </span>
                      <b className="text-[#234B4B]">
                        {integer(stats.activeCaregivers)}
                      </b>
                    </div>

                    <div className="flex items-center justify-between rounded-xl bg-[#F7FBFB] p-3">
                      <span className="text-sm text-gray-600">
                        Comptes en attente
                      </span>
                      <b className="text-[#234B4B]">
                        {integer(stats.pendingUsers)}
                      </b>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-[#D4EBEB] shadow-sm">
                <CardContent className="p-6">
                  <p className="text-xs uppercase tracking-[0.12em] font-semibold text-[#5E7D7D]">
                    À ajouter plus tard
                  </p>

                  <h2 className="mt-2 text-xl font-black text-[#234B4B]">
                    Bénéficiaires & satisfaction.... je vais rajouter quandil yaura les notes des soignants
                  </h2>

                  {/* <div className="mt-5 rounded-2xl border border-dashed border-[#AFCFCF] bg-[#FAFDFD] p-5">
                    <p className="text-sm font-semibold text-[#234B4B]">
                      Ces indicateurs ne sont pas encore calculés.
                    </p>

                    <p className="mt-2 text-xs leading-5 text-gray-500">
                      Le backend des prestations fourni ne renvoie pas
                      d'identifiant bénéficiaire/patient ni de note de
                      satisfaction. Ils seront ajoutés lorsque ces modules
                      existeront réellement, afin de ne pas afficher de
                      données inventées.
                    </p>
                  </div> */}
                </CardContent>
              </Card>
            </section>
          </>
        )}
      </div>
    </Layout>
  );
}
